import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { P2PHeader, P2PPage } from "@/components/p2p/P2PSubNav";
import { Drawer, EmptyRow, Field, Notice, Segmented, SkeletonRows, inputCls } from "@/components/paisa/ui";
import { api } from "@/lib/api";

const fmtINR = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const errText = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

const STATUS_TONE = {
  active: "bg-up/[0.12] text-up",
  paused: "bg-warning/15 text-warning",
  cancelled: "bg-down/[0.12] text-down",
  completed: "bg-primary/15 text-primary",
};

export default function P2PMyAds() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [ads, setAds] = useState(null);
  const [creating, setCreating] = useState(params.get("action") === "create");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/p2p/ads/mine");
      setAds(data.ads || []);
    } catch (e) {
      setError(errText(e));
      setAds((a) => a || []);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function act(adId, kind) {
    if (kind === "cancel" && !window.confirm("Cancel this offer? Escrowed crypto will be refunded.")) return;
    try {
      await api.post(`/api/p2p/ads/${adId}/${kind}`);
      setError("");
      load();
    } catch (e) {
      setError(errText(e));
    }
  }

  function closeCreate(refresh) {
    setCreating(false);
    if (params.get("action")) nav("/p2p/my-ads", { replace: true });
    if (refresh) load();
  }

  return (
    <AppShell bare>
      <P2PPage>
        <P2PHeader subtitle="Your listings on the P2P book. Sell offers escrow their crypto as soon as they go live.">
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="myads-create"
            className="brand-gradient rounded-lg px-3.5 py-2 text-xs font-semibold text-primary-foreground"
          >
            Post an offer
          </button>
        </P2PHeader>

        {error && <Notice tone="error" className="mt-4">{error}</Notice>}

        <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-surface/40">
          <div className="min-w-[860px]">
            <div className="table-head grid grid-cols-[1fr_1fr_1.2fr_1.2fr_1fr_90px_120px] gap-3 px-4 py-2.5">
              <span>Offer</span>
              <span className="text-right">Price</span>
              <span className="text-right">Available / total</span>
              <span className="text-right">Limits</span>
              <span>Orders</span>
              <span>Status</span>
              <span />
            </div>
            {ads === null ? (
              <SkeletonRows />
            ) : ads.length === 0 ? (
              <EmptyRow>No offers yet. Post one to start trading on the P2P book.</EmptyRow>
            ) : (
              ads.map((a) => (
                <div
                  key={a.ad_id}
                  data-testid={`myad-row-${a.ad_id}`}
                  className="terminal-row grid-cols-[1fr_1fr_1.2fr_1.2fr_1fr_90px_120px] gap-3 px-4 py-3 text-sm"
                >
                  <span>
                    <span className={`capitalize ${a.side === "sell" ? "text-down" : "text-up"}`}>{a.side}</span>{" "}
                    {a.asset}/{a.fiat}
                  </span>
                  <span className="num text-right">
                    ₹{fmtINR(a.price)}
                    {a.price_type === "floating" && (
                      <span className="block text-[11px] text-muted-foreground">{a.margin_pct}% vs market</span>
                    )}
                  </span>
                  <span className="num text-right">
                    {Number(a.available_amount).toFixed(2)} / {Number(a.total_amount).toFixed(2)}
                  </span>
                  <span className="num text-right text-xs text-muted-foreground">
                    ₹{fmtINR(a.min_order_inr)} – ₹{fmtINR(a.max_order_inr)}
                  </span>
                  <span className="num text-xs text-muted-foreground">
                    {a.active_orders_count || 0} active · {a.completed_orders_count || 0} done
                  </span>
                  <span className={`w-fit rounded-md px-2 py-0.5 text-xs capitalize ${STATUS_TONE[a.status] || "bg-surface-2 text-muted-foreground"}`}>
                    {a.status}
                  </span>
                  <span className="flex justify-end gap-1.5">
                    {a.status === "active" && (
                      <button type="button" onClick={() => act(a.ad_id, "pause")} data-testid={`pause-${a.ad_id}`} className="rounded-lg border border-border px-2 py-1 text-xs text-muted-foreground transition hover:border-primary hover:text-foreground">
                        Pause
                      </button>
                    )}
                    {a.status === "paused" && (
                      <button type="button" onClick={() => act(a.ad_id, "resume")} data-testid={`resume-${a.ad_id}`} className="rounded-lg border border-up/40 px-2 py-1 text-xs text-up transition hover:bg-up/10">
                        Resume
                      </button>
                    )}
                    {(a.status === "active" || a.status === "paused") && (
                      <button type="button" onClick={() => act(a.ad_id, "cancel")} data-testid={`cancel-${a.ad_id}`} className="rounded-lg border border-down/40 px-2 py-1 text-xs text-down transition hover:bg-down/10">
                        Cancel
                      </button>
                    )}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {creating && <CreateAdDrawer onClose={closeCreate} />}
      </P2PPage>
    </AppShell>
  );
}

function CreateAdDrawer({ onClose }) {
  const [pms, setPms] = useState(null);
  const [form, setForm] = useState({
    side: "sell",
    asset: "USDT",
    fiat: "INR",
    price_type: "fixed",
    price: "",
    margin_pct: "0",
    total_amount: "",
    min_order_inr: "500",
    max_order_inr: "50000",
    payment_method_ids: [],
    payment_window_min: 15,
    terms: "",
    auto_reply: "",
    filter_kyc_tier: 0,
    filter_min_completed_trades: 0,
    filter_min_completion_rate: 0,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api
      .get("/api/p2p/payment-methods")
      .then(({ data }) => setPms(data.payment_methods || []))
      .catch(() => setPms([]));
  }, []);

  const togglePm = (id) =>
    setForm((f) => ({
      ...f,
      payment_method_ids: f.payment_method_ids.includes(id) ? f.payment_method_ids.filter((x) => x !== id) : [...f.payment_method_ids, id],
    }));

  async function submit() {
    setBusy(true);
    setError("");
    try {
      const payload = { ...form, payment_window_min: Number(form.payment_window_min) };
      if (payload.price_type === "fixed") delete payload.margin_pct;
      else delete payload.price;
      await api.post("/api/p2p/ads", payload);
      onClose(true);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      title="Post an offer"
      subtitle="Offers go live on the P2P book as soon as you post them."
      onClose={() => onClose(false)}
      testId="ad-drawer"
      footer={
        <button
          type="button"
          onClick={submit}
          disabled={busy || !form.payment_method_ids.length || !form.total_amount}
          data-testid="ad-submit"
          className="brand-gradient w-full rounded-lg py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-40"
        >
          {busy ? "Posting…" : "Post offer"}
        </button>
      }
    >
      <div className="space-y-4">
        <Field label="I want to">
          <div className="flex w-fit gap-1 rounded-lg border border-border bg-surface-2 p-1">
            {["sell", "buy"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setForm((f) => ({ ...f, side: s }))}
                data-testid={`ad-side-${s}`}
                className={`rounded-md px-4 py-1.5 text-sm font-medium capitalize transition ${
                  form.side === s ? (s === "buy" ? "bg-up/20 text-up" : "bg-down/20 text-down") : "text-muted-foreground"
                }`}
              >
                {s} USDT
              </button>
            ))}
          </div>
        </Field>

        <Field label="Price type">
          <Segmented
            options={[
              { value: "fixed", label: "Fixed price" },
              { value: "floating", label: "Floating vs market" },
            ]}
            value={form.price_type}
            onChange={(v) => setForm((f) => ({ ...f, price_type: v }))}
            testIdPrefix="ad-pricetype"
          />
        </Field>

        {form.price_type === "fixed" ? (
          <Field label="Price (INR per USDT)">
            <input type="number" step="0.01" value={form.price} onChange={set("price")} placeholder="89.50" data-testid="ad-price" className={`num ${inputCls}`} />
          </Field>
        ) : (
          <Field label="Margin vs market (%)" hint="1.5 means 1.5% above market; -0.5 means below.">
            <input type="number" step="0.1" value={form.margin_pct} onChange={set("margin_pct")} data-testid="ad-margin" className={`num ${inputCls}`} />
          </Field>
        )}

        <Field
          label="Total USDT for this offer"
          hint={form.side === "sell" ? "This amount is moved into escrow from your wallet when the offer goes live." : undefined}
        >
          <input type="number" step="0.01" value={form.total_amount} onChange={set("total_amount")} placeholder="1000" data-testid="ad-total" className={`num ${inputCls}`} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Min order (₹)">
            <input type="number" value={form.min_order_inr} onChange={set("min_order_inr")} data-testid="ad-min" className={`num ${inputCls}`} />
          </Field>
          <Field label="Max order (₹)">
            <input type="number" value={form.max_order_inr} onChange={set("max_order_inr")} data-testid="ad-max" className={`num ${inputCls}`} />
          </Field>
        </div>

        <Field label="Payment methods">
          {pms === null ? (
            <div className="skeleton h-10 w-full" />
          ) : pms.length === 0 ? (
            <Notice tone="warn">
              You have no saved payment methods.{" "}
              <Link to="/p2p/payment-methods" className="underline">
                Add one first
              </Link>
              .
            </Notice>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {pms.map((p) => (
                <button
                  key={p.pm_id}
                  type="button"
                  onClick={() => togglePm(p.pm_id)}
                  data-testid={`ad-pm-${p.pm_id}`}
                  className={`rounded-full border px-3 py-1 text-xs transition ${
                    form.payment_method_ids.includes(p.pm_id) ? "border-primary text-foreground" : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {p.display_name} · {p.type}
                </button>
              ))}
            </div>
          )}
        </Field>

        <Field label="Payment window (minutes)">
          <input type="number" min="10" max="60" value={form.payment_window_min} onChange={set("payment_window_min")} data-testid="ad-window" className={`num ${inputCls}`} />
        </Field>

        <Field label="Terms shown to the counterparty (optional)">
          <textarea
            value={form.terms}
            onChange={set("terms")}
            maxLength={500}
            rows={3}
            placeholder="Pay only from your own bank account. Share the UTR in chat."
            data-testid="ad-terms"
            className={inputCls}
          />
        </Field>

        {error && <Notice tone="error">{error}</Notice>}
      </div>
    </Drawer>
  );
}
